<template>
  <div class="interaction-header">
    <div class="interaction-header__hero">
      <div class="contact-summary">
        <div class="contact-avatar">
          <img
            v-if="contact?.profilePictureUrl"
            :src="contact.profilePictureUrl"
            :alt="fullName"
            class="contact-avatar__img"
          />
          <span v-else>{{ initials }}</span>
        </div>
        <div class="contact-meta">
          <h5 class="contact-name">{{ fullName }}</h5>
          <p class="contact-role">{{ contact?.jobTitle || "—" }}</p>
        </div>
      </div>
      <button class="close-panel-btn" type="button" @click="$emit('close')">
        <Icon icon="mdi:close" width="18" height="18" />
      </button>
    </div>
    <div class="interaction-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.id"
        :class="['interaction-tab', { active: tab.id === activeTab }]"
        type="button"
        @click="$emit('update:activeTab', tab.id)"
      >
        <span class="interaction-tab__icon-wrap">
          <Icon :icon="tab.icon" width="14" height="14" />
          <span v-if="tab.badge > 0" class="tab-badge">
            {{ tab.badge > 9 ? "9+" : tab.badge }}
          </span>
        </span>
        <span>{{ tab.label }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";

export default {
  name: "CallInteractionHeader",
  components: { Icon },
  props: {
    contact: {
      type: Object,
      default: null,
    },
    tabs: {
      type: Array,
      default: () => [],
    },
    activeTab: {
      type: String,
      default: "calls",
    },
  },
  emits: ["update:activeTab", "close"],
  computed: {
    fullName() {
      if (!this.contact) return "—";
      const fname = this.contact.fname ? this.$filters.capitalize(this.contact.fname) : "";
      const lname = this.contact.lname ? this.$filters.capitalize(this.contact.lname) : "";
      return `${fname} ${lname}`.trim() || "—";
    },
    initials() {
      if (!this.contact) return "SJ";
      const fname = this.contact.fname ? this.contact.fname[0] : "";
      const lname = this.contact.lname ? this.contact.lname[0] : "";
      const chars = `${fname}${lname}`.toUpperCase();
      return chars || "SJ";
    },
  },
};
</script>

<style scoped>
.interaction-header {
  border-bottom: 1px solid #ececfb;
}

.interaction-header__hero {
  background: linear-gradient(90deg, #2d3bff 0%, #4f63ff 100%);
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.contact-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.contact-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
}

.contact-avatar img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}

.contact-name {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.contact-role {
  margin: 0;
  font-size: 0.8rem;
  opacity: 0.9;
}

.close-panel-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.2s ease;
}

.close-panel-btn:hover {
  background: rgba(255, 255, 255, 0.35);
}

.interaction-tabs {
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  background: #fff;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

.interaction-tabs::-webkit-scrollbar {
  height: 4px;
}

.interaction-tabs::-webkit-scrollbar-track {
  background: transparent;
}

.interaction-tabs::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 2px;
}

.interaction-tabs::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.interaction-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: #f4f5ff;
  color: #5b5fc7;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition:
    background 0.2s ease,
    color 0.2s ease;
  flex-shrink: 0;
  white-space: nowrap;
}

.interaction-tab.active {
  background: #2d3bff;
  color: #fff;
  box-shadow: 0 4px 14px rgba(45, 59, 255, 0.2);
}

.interaction-tab:hover:not(.active) {
  background: #e3e6ff;
}

.interaction-tab__icon-wrap {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.tab-badge {
  position: absolute;
  top: -7px;
  right: -7px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 999px;
  border: 1.5px solid #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
